import type { AssetWithMatchScore, InterestAssetMatchScore, ParsedAsset } from '@aikoa/common'
import { useTranslations } from '@aikoa/i18n/react'
import { memo, useCallback, useMemo, useRef, useState } from 'react'
import { Pressable, View } from 'react-native'
import { ActionButton } from '~/components/Actions'
import { PropertyListItem, PropertyRowCompact } from '~/components/assets/PropertyListItem.tsx'
import { SectionContent } from '~/components/layout/Sections'
import { Label } from '~/components/typography/Content'
import { Heading3 } from '~/components/typography/Titles'
import BottomSheet from '~/components/ui/bottom-sheet'
import { Button } from '~/components/ui/button'
import {
  BottomSheetSelect,
  type BottomSheetSelectRefHandle,
} from '~/components/ui/select-bottom-sheet'
import { cn } from '~/lib/utils'
import Icon from '../Icon'
import { AddPropertyFlow } from './AddPropertyFlow'
import { PropertyOption } from './PropertyCard'

const ADD_PROPERTY_ID = '__add_new_property__'

type AddPropertyItem = {
  id: typeof ADD_PROPERTY_ID
  isAddNew: true
}

type AssetSelectItem = {
  asset: ParsedAsset
  matchScores?: InterestAssetMatchScore | null
}

type SelectItem = AssetSelectItem | AddPropertyItem

function isAddPropertyItem(item: SelectItem): item is AddPropertyItem {
  return 'isAddNew' in item && item.isAddNew === true
}

type PropertyMultiSelectProps = {
  assets?: (ParsedAsset | AssetWithMatchScore)[]
  isLoading?: boolean
  selectedAssetIds: string[]
  onChangeSelectedAssetIds: (ids: string[]) => void
  label?: string
  sheetTitle?: string
  className?: string
  triggerClassName?: string
  multiple?: boolean
  disabledAssetIds?: string[]
  showCreateButton?: boolean
  size?: 'sm' | 'default'
  modalKey?: string
  onFetchNextPage?: () => void
  hasNextPage?: boolean
  isFetchingNextPage?: boolean
}

export const PropertySelect = memo(function PropertySelect({
  assets,
  isLoading,
  selectedAssetIds,
  onChangeSelectedAssetIds,
  multiple = false,
  label,
  sheetTitle,
  className,
  triggerClassName,
  disabledAssetIds,
  showCreateButton = true,
  size = 'default',
  modalKey = 'property-select',
  onFetchNextPage,
  hasNextPage,
  isFetchingNextPage,
}: PropertyMultiSelectProps) {
  const { t } = useTranslations(['property', 'common'])
  const [isAddPropertyOpen, setAddPropertyOpen] = useState(false)
  const sanitizedSelectedAssetIds = selectedAssetIds.filter(Boolean)

  const defaultLabel = t(($) => $.select.selectProperty)
  const defaultSheetTitle = t(($) => $.select.selectProperty)

  const onSelectToggle = useCallback(
    (assetId: string) => {
      if (!multiple) {
        onChangeSelectedAssetIds([assetId])
        return
      }

      const exists = sanitizedSelectedAssetIds.includes(assetId)

      const next = exists
        ? sanitizedSelectedAssetIds.filter((id) => id !== assetId)
        : [...sanitizedSelectedAssetIds, assetId]

      onChangeSelectedAssetIds(next)
    },
    [multiple, onChangeSelectedAssetIds, sanitizedSelectedAssetIds]
  )

  const assetData = useMemo(
    () =>
      (assets || []).map((asset) => ({
        asset: 'asset' in asset ? asset.asset : asset,
        matchScores: 'matchScores' in asset ? asset.matchScores : null,
      })),
    [assets]
  )

  const selectData = useMemo<SelectItem[]>(() => {
    if (!showCreateButton) {
      const addNewItem: AddPropertyItem = {
        id: ADD_PROPERTY_ID,
        isAddNew: true,
      }

      return [addNewItem, ...assetData]
    }

    return assetData
  }, [showCreateButton, assetData])

  const hasAssets = assetData.length > 0
  const showSelect = hasAssets || !showCreateButton

  const bottomSheetRef = useRef<BottomSheetSelectRefHandle>(null)

  const keyExtractor = useCallback((item: SelectItem) => {
    if (isAddPropertyItem(item)) {
      return item.id
    }

    return item.asset.id
  }, [])

  const getItemType = useCallback((item: SelectItem) => {
    if (isAddPropertyItem(item)) {
      return 'add-property'
    }

    return 'property'
  }, [])

  return (
    <>
      <View className={cn('gap-4', className)}>
        {showSelect && (
          <BottomSheetSelect<SelectItem>
            ref={bottomSheetRef}
            modalKey={modalKey}
            data={selectData}
            keyExtractor={keyExtractor}
            multiple={multiple}
            values={multiple ? sanitizedSelectedAssetIds : undefined}
            value={
              !multiple && sanitizedSelectedAssetIds.length > 0
                ? sanitizedSelectedAssetIds[0]
                : undefined
            }
            onChangeValues={multiple ? (ids) => onChangeSelectedAssetIds(ids) : undefined}
            onChange={
              !multiple
                ? (item) => !isAddPropertyItem(item) && onChangeSelectedAssetIds([item.asset.id])
                : undefined
            }
            label={
              !hasAssets && showCreateButton
                ? t(($) => $.select.noProperties)
                : (label ?? defaultLabel)
            }
            disabled={!hasAssets && showCreateButton}
            sheetTitle={sheetTitle ?? defaultSheetTitle}
            footer={
              !multiple
                ? undefined
                : ({ close }) => (
                    <Button variant="primary" size="lg" onPress={close}>
                      {t(($) => $.actions.close)}
                    </Button>
                  )
            }
            getItemType={getItemType}
            waitForInitialLayout
            isLoading={isLoading}
            estimatedItemSize={size === 'sm' ? 56 : 115}
            onEndReached={hasNextPage ? onFetchNextPage : undefined}
            onEndReachedThreshold={1}
            isFetchingNextPage={isFetchingNextPage}
            className={cn(
              'hover:bg-muted active:bg-muted/50',
              size === 'sm' && 'native:h-10 py-2',
              triggerClassName
            )}
            renderItem={({ item, onSelect, selected, context }) => {
              if (isAddPropertyItem(item)) {
                return context === 'option' ? (
                  <AddPropertyOption onPress={() => setAddPropertyOpen(true)} />
                ) : (
                  <SectionContent className="px-2">{label}</SectionContent>
                )
              }

              const isDisabled = disabledAssetIds?.includes(item.asset.id)
              const selectable = !isDisabled && !selected

              return context === 'option' ? (
                <PropertyOption
                  asset={item.asset}
                  onPress={selectable ? () => onSelect() : undefined}
                  isSelected={selected}
                  isDisabled={!selectable}
                  matchScores={item.matchScores}
                  buttonLabel={
                    selected ? t(($) => $.actions.removeSelection) : t(($) => $.actions.select)
                  }
                  className={cn('mb-4')}
                />
              ) : sanitizedSelectedAssetIds.length > 0 ? (
                <View className="pointer-events-none flex-1">
                  {assetData
                    .filter(({ asset }) => sanitizedSelectedAssetIds.includes(asset.id))
                    .slice(0, 1)
                    .map(({ asset }) =>
                      size === 'sm' ? (
                        <PropertyRowCompact key={asset.id} asset={asset} />
                      ) : (
                        <PropertyListItem
                          key={asset.id}
                          asset={asset}
                          organisationName={asset.organisation?.name}
                          realtorName={asset.organisation?.realtorName}
                          className="shadow-none! pointer-events-none m-0 max-w-none border-none bg-transparent"
                          isSelected
                          isPressable={false}
                        />
                      )
                    )}
                  {sanitizedSelectedAssetIds.length > 1 && (
                    <View className="mb-1.5 ml-1.5 mt-2 flex-row items-center justify-center gap-2 rounded-md border border-tertiary-foreground/60 bg-tertiary-foreground/50 px-3 py-2">
                      <Label>
                        {t(($) => $.select.andOther)} {sanitizedSelectedAssetIds.length - 1}{' '}
                        {sanitizedSelectedAssetIds.length - 1 === 1
                          ? t(($) => $.select.otherProperty)
                          : t(($) => $.select.otherProperties)}
                      </Label>
                    </View>
                  )}
                </View>
              ) : (
                <SectionContent className="px-2">{label ?? defaultLabel}</SectionContent>
              )
            }}
          />
        )}

        {showCreateButton && hasAssets && (
          <Label className="text-center font-medium text-lg uppercase">
            {t(($) => $.select.or)}
          </Label>
        )}

        {showCreateButton && (
          <ActionButton
            className="rounded-xl border-2 border-muted px-4 py-5 hover:bg-muted"
            action={{
              key: 'create-property',
              label: t(($) => $.actions.addNewProperty),
              description: hasAssets
                ? t(($) => $.select.createNew)
                : t(($) => $.select.noProperties),
              icon: 'home',
              iconSize: 20,
              actionType: 'add',
              onPress: () => setAddPropertyOpen(true),
              theme: '#B8DBDB',
              buttonVariant: 'primary',
              className: 'gap-1',
            }}
          />
        )}
      </View>
      <BottomSheet
        modalKey="property-select-create"
        showCloseButton={false}
        showHandle={false}
        isOpen={isAddPropertyOpen}
        stackBehavior="push"
        onOpenChange={setAddPropertyOpen}>
        <AddPropertyFlow
          className="max-h-[90vh] flex-1 sm:max-h-[75vh]"
          onFlowComplete={(newAssetId) => {
            setAddPropertyOpen(false)
            onSelectToggle(newAssetId)
            bottomSheetRef.current?.close()
          }}
          onClose={() => {
            setAddPropertyOpen(false)
          }}
          isInBottomSheet
        />
      </BottomSheet>
    </>
  )
})

const AddPropertyOption = memo(function AddPropertyOption({ onPress }: { onPress: () => void }) {
  const { t } = useTranslations('property')
  return (
    <Pressable
      onPress={onPress}
      className="group/button native:shadow-card mx-2 mb-4 flex-row items-center gap-4 rounded-xl bg-card px-4 py-4 shadow-sm shadow-primary/20 transition-colors duration-150 hover:bg-accent active:bg-accent">
      <View className="items-center justify-center rounded-lg bg-muted p-3 transition-colors duration-150 group-hover/button:bg-accent">
        <Icon
          name="add"
          size={24}
          className="text-foreground transition-colors duration-150 group-hover/button:text-accent-foreground"
        />
      </View>
      <Heading3 className="transition-colors duration-150 group-hover/button:text-accent-foreground">
        {t(($) => $.actions.addNewProperty)}
      </Heading3>
    </Pressable>
  )
})

export default PropertySelect
